import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import incomeShot from '../../assets/lander-income-attribution.png';
import rollShot from '../../assets/lander-position-roll.png';
import screenerShot from '../../assets/lander-screener-score.png';
import dashboardShot from '../../assets/lander-dashboard.jpeg';
import '../../styles/features/real-yield.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Same key as the /coach client, so an anonymous visitor is one id across both
// public surfaces (cross-page funnel stitching).
const LS_SESSION = 'babylon_coach_session';

/** Stable anonymous session id for server-side funnel stitching. Ephemeral
 *  fallback when localStorage is blocked. */
function getFunnelSessionId(): string {
  const fresh = () =>
    `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  try {
    let id = localStorage.getItem(LS_SESSION);
    if (!id) {
      id = window.crypto?.randomUUID?.() || fresh();
      localStorage.setItem(LS_SESSION, id);
    }
    return id;
  } catch {
    return fresh();
  }
}

/** Best-effort funnel beacon → public_coach_events (surface='real_yield'). Lets
 *  the reach → trial-click → email funnel be read by UTM. Never throws. */
function trackFunnel(
  eventType: 'pageview' | 'register_click',
  attribution: {
    session_id: string;
    utm_source: string | null;
    utm_campaign: string | null;
    referrer: string | null;
  },
): void {
  try {
    fetch(`${API_URL}/api/public/coach/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ event_type: eventType, surface: 'real_yield', ...attribution }),
    }).catch(() => {
      /* best-effort */
    });
  } catch {
    /* best-effort */
  }
}

/** Stagger helper for the load-in reveal. */
const reveal = (step: number) =>
  ({ '--d': `${step * 90}ms` } as React.CSSProperties);

/** Pain keys — an ad deep-links via ?pain=<key> to lead with the matching panel + hero. */
type PainKey = 'income' | 'roll' | 'screener' | 'dashboard';
const PAIN_KEYS: PainKey[] = ['income', 'roll', 'screener', 'dashboard'];

type Panel = {
  key: PainKey;
  eyebrow: string;
  heading: React.ReactNode;
  body: string;
  shot: string;
  alt: string;
  flip?: boolean;
  /** Landscape screenshots stack full-width (caption above) so detail stays legible. */
  wide?: boolean;
};

/** Per-pain hero — H1 matches the ad's hook; deck always keeps the $18,069 proof for trust. */
const heroContent = (
  pain: PainKey | null,
  isWheel: boolean
): { h1: React.ReactNode; deck: React.ReactNode } => {
  const proof = <span className="rv-mark">$18,069 of my $40,498</span>;
  switch (pain) {
    case 'roll':
      return {
        h1: (
          <>
            Roll it, or let it get <span className="rv-mark">called away</span>?
          </>
        ),
        deck: (
          <>
            The hardest call in covered calls — made the same way every time, by your rule, not a
            hunch. Babylon watches each position against your profit target, time stop and defensive
            delta and tells you the moment one fires. I built it on the account where I found {proof}{' '}
            of my "income" was phantom — and realized I needed rules, not feel.
          </>
        ),
      };
    case 'screener':
      return {
        h1: (
          <>
            Stop <span className="rv-mark">guessing</span> which call to sell.
          </>
        ),
        deck: (
          <>
            A 100-point read on every name — the quality of the premium, the quality of the timing,
            and how this exact setup has paid across hundreds of past trades, with an earnings
            warning before you get trapped. I built it on the account where I found {proof} of my
            "income" was phantom.
          </>
        ),
      };
    case 'dashboard':
      return {
        h1: (
          <>
            Your whole book, in one <span className="rv-mark">honest</span> glance.
          </>
        ),
        deck: (
          <>
            Every position, what's at risk, what to roll or close, your real yield and theta per day
            — one screen, every morning. I built it on my own account, after I found {proof} of my
            "income" was phantom and needed to see the truth at a glance.
          </>
        ),
      };
    default: // 'income' or no pain — the phantom-income hook
      return {
        h1: isWheel ? (
          <>
            Is the wheel <span className="rv-mark">actually beating</span> just holding your shares?
          </>
        ) : (
          <>
            Is your income ETF <span className="rv-mark">quietly eating</span> your principal?
          </>
        ),
        deck: (
          <>
            You can't answer that until your "income" number is honest. Most aren't — on my own
            covered-call account, <span className="rv-mark">$18,069 of $40,498</span> I'd been
            calling "income" was phantom. Babylon is the tool I built to fix that. Here's what it
            does.
          </>
        ),
      };
  }
};

const PANELS: Panel[] = [
  {
    key: 'income',
    eyebrow: 'The honest number',
    heading: (
      <>
        You call it income. How much was <span className="rv-mark">actually the option</span>?
      </>
    ),
    body: "Babylon splits every trade into what the premium really earned and what was just your stock moving — scored against simply holding the shares. No more counting your own gains twice and calling it yield.",
    shot: incomeShot,
    alt: 'Babylon trade attribution — loss-cushioned, premium-winner and stock-winner buckets, each splitting the stock move from the option contribution versus the S&P over the same period.',
    wide: true,
  },
  {
    key: 'roll',
    eyebrow: 'The decision',
    heading: <>Roll, or let it get called away?</>,
    body: 'Set your rule once — profit target, time stop, defensive delta. Babylon watches every position against it and tells you the moment it fires. Your discipline, executed — not a tip.',
    shot: rollShot,
    alt: 'Babylon position card for TTMI — a Keep My Shares intent, a Roll rule with profit target, time stop and defensive delta, a price chart marking the stops, and a full P&L split into stock leg, option leg, theta income, stock move and IV move.',
    flip: true,
  },
  {
    key: 'screener',
    eyebrow: 'The next trade',
    heading: (
      <>
        Which name — and is <span className="rv-mark">now</span> the time to sell?
      </>
    ),
    body: 'A 100-point read on every stock: quality of the premium, quality of the timing, and how this exact setup has paid across hundreds of past trades. Plus an earnings warning before you get trapped holding through one.',
    shot: screenerShot,
    alt: 'Babylon screener card for RKLB — Quality 50/50 and Timing 36/50 scores, annual yield, historical volatility and trend, a Hot Uptrend / Strong Setup classification with historical P&L, and an earnings-in-39-days warning.',
  },
  {
    key: 'dashboard',
    eyebrow: 'The whole book',
    heading: <>Open it once. See exactly what needs you today.</>,
    body: "Every position, what's at risk, what to roll or close, your real yield and your theta-per-day — one screen, every morning. No spreadsheet, no guessing what changed overnight.",
    shot: dashboardShot,
    alt: "Babylon portfolio dashboard — portfolio size, month-to-date and unrealized P&L, theta per day, a fear gauge and market-trend panel, today's top movers, screener quick picks, and a Portfolio-at-a-Glance list flagging which positions need attention.",
    wide: true,
  },
];

// Email-first ask (Jun 2026 sprint): a low-friction, value-backed email for the
// phantom-income breakdown — matches the ad's promise and IS the sprint's one
// metric (wtp_intents). Trial signup is the secondary path. 'primary' styles it
// as the hero's main CTA; 'slim' is the quieter capture under the closing trial.
// Declared at module scope on purpose: defined inside RealYieldLanding it became a
// new component type on every render, remounting the <input> on each keystroke
// (focus lost after every character). State lives in the page and comes in as props.
interface EmailCaptureProps {
  id: string;
  label: React.ReactNode;
  variant: 'primary' | 'slim';
  email: string;
  onEmailChange: (value: string) => void;
  emailValid: boolean;
  submitting: boolean;
  submitted: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
}

const EmailCapture: React.FC<EmailCaptureProps> = ({
  id,
  label,
  variant,
  email,
  onEmailChange,
  emailValid,
  submitting,
  submitted,
  error,
  onSubmit,
}) => {
  const isPrimary = variant === 'primary';
  if (submitted) {
    return (
      <p className={isPrimary ? 'rv-capture-done' : 'rv-fallback-done'}>
        Done — I'll send the phantom-income breakdown to your inbox shortly. Nothing else.
      </p>
    );
  }
  return (
    <form className={isPrimary ? 'rv-capture' : 'rv-fallback'} onSubmit={onSubmit}>
      <label className={isPrimary ? 'rv-capture-label' : 'rv-fallback-label'} htmlFor={id}>
        {label}
      </label>
      <div className={isPrimary ? 'rv-capture-row' : 'rv-fallback-row'}>
        <input
          id={id}
          className="rv-email-input"
          type="email"
          maxLength={255}
          autoComplete="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
        />
        <button
          className={isPrimary ? 'rv-capture-btn' : 'rv-fallback-btn'}
          type="submit"
          disabled={!emailValid || submitting}
        >
          {submitting ? 'Sending…' : 'Send me the breakdown'}
        </button>
      </div>
      {isPrimary && (
        <p className="rv-capture-micro">One email with the method. No spam, unsubscribe anytime.</p>
      )}
      {error && (
        <p className="rv-error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
};

const RealYieldLanding: React.FC = () => {
  const [searchParams] = useSearchParams();
  // Wheel-cohort doorway (r/Optionswheel inbound) swaps the H1 only
  const isWheelVariant = searchParams.get('v') === 'wheel';
  // Pain-matched entry (?pain=roll etc.): lead with that panel + a matching hero,
  // the rest follow on scroll. One page, one pooled funnel — no measurement split.
  const painParam = searchParams.get('pain');
  const pain: PainKey | null = PAIN_KEYS.includes(painParam as PainKey)
    ? (painParam as PainKey)
    : null;
  const orderedPanels = pain
    ? [...PANELS.filter((p) => p.key === pain), ...PANELS.filter((p) => p.key !== pain)]
    : PANELS;
  const hero = heroContent(pain, isWheelVariant);

  // Attribution (Jun 2026 sprint): founder-account links + Reddit Ads carry UTMs
  const utmSource = searchParams.get('utm_source');
  const utmCampaign = searchParams.get('utm_campaign');

  // Trial signup is the primary CTA; carry attribution through to /register.
  const trialHref = (() => {
    const p = new URLSearchParams({ plan: 'pro' });
    if (utmSource) p.set('utm_source', utmSource.slice(0, 60));
    if (utmCampaign) p.set('utm_campaign', utmCampaign.slice(0, 120));
    return `/register?${p.toString()}`;
  })();

  // Funnel beacons (reach → trial-click), surface='real_yield', by UTM.
  const sessionIdRef = useRef<string>(getFunnelSessionId());
  const buildAttribution = () => ({
    session_id: sessionIdRef.current,
    utm_source: utmSource,
    utm_campaign: utmCampaign,
    referrer: document.referrer ? document.referrer.slice(0, 2000) : null,
  });
  const pageviewSentRef = useRef(false);
  useEffect(() => {
    if (pageviewSentRef.current) return;
    pageviewSentRef.current = true;
    trackFunnel('pageview', buildAttribution());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleTrialClick = () => trackFunnel('register_click', buildAttribution());

  // Sticky CTA reveals once the hero CTA scrolls out of view (no double-buttons).
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const el = heroCtaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setShowSticky(!entry.isIntersecting), {
      threshold: 0,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Slim email fallback — keeps the WTP intent signal alive for non-signups.
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailValid = /\S+@\S+\.\S+/.test(email.trim());

  useEffect(() => {
    document.title = 'Is your income real? — Babylon';
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute('content') ?? null;
    meta?.setAttribute(
      'content',
      'A covered-call trader fixed the math on his own account and found ~40% of his "income" was phantom. Babylon shows what\'s real, what to roll, and what to sell next — free for 14 days.'
    );
    return () => {
      if (meta && prev !== null) meta.setAttribute('content', prev);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/wtp-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          would_pay: null,
          price_feel: null,
          heard_from: null,
          email: email.trim(),
          variant: isWheelVariant ? 'wheel' : 'etf',
          referrer: document.referrer ? document.referrer.slice(0, 2000) : null,
          utm_source: utmSource ? utmSource.slice(0, 60) : null,
          utm_campaign: utmCampaign ? utmCampaign.slice(0, 120) : null,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
    } catch {
      setError("Couldn't send that — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const PrimaryCTA: React.FC<{ style?: React.CSSProperties; label?: string }> = ({
    style,
    label = 'Try Pro free for 14 days',
  }) => (
    <Link className="rv-cta" to={trialHref} style={style} onClick={handleTrialClick}>
      <span>{label}</span>
      <span className="rv-cta-sub">No card — full Pro access</span>
    </Link>
  );

  const captureProps = {
    email,
    onEmailChange: setEmail,
    emailValid,
    submitting,
    submitted,
    error,
    onSubmit: handleSubmit,
  };

  return (
    <div className="rv-page">
      <main className="rv-container">
        {/* Wordmark */}
        <div className="rv-wordmark rv-reveal" style={reveal(0)}>
          babylon<span>.markets</span>
        </div>

        {/* Hero */}
        <header className="rv-hero">
          <h1 className="rv-h1 rv-reveal" style={reveal(1)}>
            {hero.h1}
          </h1>
          <p className="rv-deck rv-reveal" style={reveal(2)}>
            {hero.deck}
          </p>
          <div className="rv-hero-cta rv-reveal" style={reveal(3)} ref={heroCtaRef}>
            <EmailCapture
              id="rv-email-hero"
              variant="primary"
              {...captureProps}
              label={
                <>
                  Where should I send it? I'll email you the exact method I used to find{' '}
                  <span className="rv-mark">$18,069 of phantom "income"</span> in my own account —
                  and how to check yours.
                </>
              }
            />
            <Link className="rv-secondary-link" to={trialHref} onClick={handleTrialClick}>
              Or skip ahead — try Pro free for 14 days, no card{' '}
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </header>

        <div className="rv-split-rule rv-reveal" style={reveal(4)} aria-hidden="true">
          <span className="rv-split-phantom" />
          <span className="rv-split-real" />
        </div>

        {/* Panels — problem → proof → CTA */}
        <section className="rv-panels">
          {orderedPanels.map((panel, i) => (
            <article
              key={panel.eyebrow}
              className={`rv-panel${panel.wide ? ' rv-panel--wide' : panel.flip ? ' rv-panel--flip' : ''} rv-reveal`}
              style={reveal(5 + i)}
            >
              <div className="rv-panel-copy">
                <p className="rv-panel-eyebrow">{panel.eyebrow}</p>
                <h2 className="rv-panel-h2">{panel.heading}</h2>
                <p className="rv-panel-body">{panel.body}</p>
                <Link className="rv-panel-link" to={trialHref} onClick={handleTrialClick}>
                  Try Pro free <span aria-hidden="true">→</span>
                </Link>
              </div>
              <div className="rv-panel-shot">
                <img className="rv-shot-img" src={panel.shot} alt={panel.alt} loading="lazy" />
              </div>
            </article>
          ))}
        </section>

        {/* Closing CTA band */}
        <section className="rv-close rv-reveal" style={reveal(9)}>
          <h2 className="rv-close-h2">See the honest number on your own positions.</h2>
          <p className="rv-close-sub">
            Connect nothing, risk nothing. 14 days of full Pro — the real-yield split, the roll
            rules, the screener, the whole dashboard. No card until you decide it's worth it.
          </p>
          <PrimaryCTA style={{ maxWidth: 420, margin: '0 auto' }} />

          {/* Email-first secondary — the phantom-income breakdown, a real value
              exchange (the method, not a newsletter). This is the sprint metric. */}
          <EmailCapture
            id="rv-email-close"
            variant="slim"
            {...captureProps}
            label="Not ready for the trial? Get the phantom-income breakdown by email — the exact method I used, not a newsletter."
          />
        </section>

        {/* Caveats — honest fine print */}
        <section className="rv-caveats rv-reveal" style={reveal(10)}>
          <h3>The honest fine print:</h3>
          <ul>
            <li>
              In my own covered-call account, when I fixed the math, about 40% of what my old
              numbers called "income" turned out to be phantom — stock gains the app was
              double-counting as yield. (That was $18,069 of $40,498.)
            </li>
            <li>
              Screens show real product views with real numbers; yours will differ — this is one
              trader's history, not a projection or a guarantee.
            </li>
            <li>Marks are the official daily close; quiet contracts use the prior close.</li>
            <li>
              This isn't advice. There's no "you will," no implied return, no claim to beat any
              benchmark — just honest math on your own positions.
            </li>
          </ul>
        </section>

        <div className="rv-split-rule rv-split-rule--faint" aria-hidden="true">
          <span className="rv-split-phantom" />
          <span className="rv-split-real" />
        </div>

        <footer className="rv-footer">
          Built by one covered-call trader who got tired of lying to himself about his returns. Not
          advice. Not a fund. Just honest math on your own positions. © 2026 Babylon.
        </footer>
      </main>

      {/* Sticky CTA — slides in once the hero button is scrolled past */}
      <Link
        className={`rv-sticky-cta${showSticky ? ' is-visible' : ''}`}
        to={trialHref}
        onClick={handleTrialClick}
        aria-hidden={!showSticky}
        tabIndex={showSticky ? 0 : -1}
      >
        <span className="rv-sticky-note">Stop counting your stock gains as income.</span>
        <span className="rv-sticky-pill">
          Try Pro free — 14 days, no card <span aria-hidden="true">→</span>
        </span>
      </Link>
    </div>
  );
};

export default RealYieldLanding;
